import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

export type ToastType = 'error' | 'success' | 'info' | 'warning';

type ToastBase = {
    message?: string;
    title?: string;
    type: ToastType;
};

type DismissibleToast = ToastBase & {
    dismissible: true;
    timeout?: number;
};

type TimeoutToast = ToastBase & {
    timeout: number;
    dismissible?: false;
};

export type ToastInput = TimeoutToast | DismissibleToast;

export type Toast = ToastInput & {
    id: string;
};

type ToastContextType = {
    toasts: Toast[];
    addToast: (toast: ToastInput) => string;
    dismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextType | null>(null);

type ToastProviderProps = {
    children: ReactNode;
};

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const dismissToast = useCallback((id: string) => {
        setToasts((all) => all.filter((t) => t.id !== id));
    }, []);

    const addToast = useCallback(
        (toast: ToastInput): string => {
            const id = Math.floor(Math.random() * 10000).toString();

            const newToast: Toast = {
                ...toast,
                id,
            };

            setToasts((all) => [newToast, ...all]);

            // If toast has a timeout, dismiss it after the specified time
            if (toast.timeout) {
                setTimeout(() => dismissToast(id), toast.timeout);
            }

            return id;
        },
        [dismissToast]
    );

    const value: ToastContextType = {
        toasts,
        addToast,
        dismissToast,
    };

    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextType {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
